﻿import React, {JSX, useCallback, useEffect, useId, useRef, useState} from "react";
import {getBoxModelWithScroll} from "../helpers/BoxModel";

export type ListBoxProps<T> = {
	onSelect: (value: T, index: number) => void;
	items: ListBoxItemProps<T>[];
	selected?: T;
};

export type ListBoxItemProps<T> = {
	label: string;
	value: T;
}

function getTarget(evt: Event, root: HTMLElement | null): HTMLElement | null {
	if (root == null) {
		return null;
	}

	let current: HTMLElement | null = evt.target as HTMLElement;
	while (current != null && current != root && root.contains(current)) {
		if (current.parentElement == root) {
			return current;
		}

		current = current.parentElement;
	}

	return null;
}

function findIndex<T>(items: ListBoxItemProps<T>[], selected?: T): number {
	return selected == null ? -1 : items.findIndex(x => x.value === selected);
}

export default function ListBox<T>({items, selected, onSelect}: ListBoxProps<T>): JSX.Element {
	const listBoxRef = useRef<HTMLUListElement | null>(null);
	const visibleItems = useRef<number>(0);

	const [selectedIndex, setSelectedIndex] = useState(findIndex(items, selected));

	useEffect(() => {
		setSelectedIndex(findIndex(items, selected));
	}, [selected, items]);

	const rootId = useId();

	const selectOptionByMouse = useCallback((e: MouseEvent) => {
		const target = getTarget(e, listBoxRef.current);
		if (target == null) {
			return;
		}

		e.preventDefault();
		const si = parseInt(target.dataset["index"]!, 10);
		setSelectedIndex(si);
		onSelect(items[si].value, si);
	}, [items, onSelect]);

	const moveCursor = useCallback((e: KeyboardEvent) => {
		const count = items.length;
		if (count === 0 || e.shiftKey) {
			return;
		}

		const visibleItemCount = visibleItems.current;
		let newPosition = selectedIndex;

		switch (e.key) {
			case "PageUp":
				newPosition -= visibleItemCount;
				break;

			case "PageDown":
				newPosition += visibleItemCount;
				break;

			case "ArrowUp":
				--newPosition;
				break;

			case "ArrowDown":
				++newPosition;
				break;

			case "End":
				newPosition = count - 1;
				break;

			case "Home":
				newPosition = 0;
				break;

			default:
				return;
		}

		e.preventDefault();

		if (newPosition >= count) {
			newPosition = count - 1;
		} else if (newPosition < 0) {
			newPosition = 0;
		}

		setSelectedIndex(newPosition);
	}, [items, selectedIndex]);

	const selectOptionByKey = useCallback((e: KeyboardEvent) => {
		const index = selectedIndex;
		if (e.key !== "Enter" || index < 0) {
			return;
		}

		e.preventDefault();
		onSelect(items[index].value, index);
	}, [items, selectedIndex, onSelect]);

	useEffect(() => {
		if (listBoxRef.current == null) {
			return;
		}

		const currentListBox = listBoxRef.current;
		currentListBox.addEventListener("mousedown", selectOptionByMouse);
		document.addEventListener("keydown", moveCursor);
		document.addEventListener("keypress", selectOptionByKey);

		const borderBox = getBoxModelWithScroll(currentListBox).borderBox;
		visibleItems.current = currentListBox.children.length == 0
			? 0
			: Math.ceil(borderBox.height / getBoxModelWithScroll(currentListBox.firstElementChild as HTMLElement).borderBox.height) - 1;

		return () => {
			document.removeEventListener("keydown", moveCursor);
			document.removeEventListener("keypress", selectOptionByKey);
			currentListBox?.removeEventListener("mousedown", selectOptionByMouse);
		}
	}, [selectOptionByMouse, moveCursor, selectOptionByKey]);

	let activeDescendant: string | undefined = undefined;
	if (selectedIndex >= 0) {
		activeDescendant = rootId + ":" + selectedIndex;
	}

	return (
		<menu className="list-box" role="listbox" tabIndex={0} ref={listBoxRef} aria-activedescendant={activeDescendant}>
			{items.map((item: ListBoxItemProps<T>, index: number) => (
				<li key={index} role="option" tabIndex={-1} id={rootId + ":" + index}
				    data-index={index}
				    aria-selected={selectedIndex === index}>{item.label}</li>))}
		</menu>
	);
};
